import { useState } from 'react'

import i18next from 'i18next'
import { useTranslation } from 'react-i18next'

import { MessageAttachments } from '../attachments/MessageAttachments'
import { VoiceNote, VoiceTranscript } from '../attachments/VoiceNote'
import { MessageBody } from '../components/MessageBody'
import { CopyButton } from '../components/CopyButton'
import { PlayIcon, RefreshIcon, WarningIcon } from '../components/icons'
import { ProfileAvatar } from '../components/ProfileAvatar'
import type { Message } from '../lib/conversations'
import { formatDuration, formatMessageTime } from '../lib/datetime'
import { composeMessageText } from '../lib/messageText'
import { Feedback, type FeedbackState } from './Feedback'
import { SpeakButton } from './SpeakButton'

/**
 * Gecmisten yuklenen mesaj.
 *
 * ## Daktiloya HIC girmiyor
 *
 * Tamamlanmis bir mesaji harf harf yazdirmak kullaniciya "uygulama gecmisi
 * yeniden yaziyor" gibi gorunuyor ve 50 mesajlik bir konusmayi acmak
 * dakikalar surerdi. `MessageBody` `streaming={false}` ile cagriliyor.
 *
 * ## `think_text` ve `tool_cards` metne GERI dokunuyor
 *
 * Sunucu bunlari ayri kolonlarda tutuyor (prompt kurarken dislamak trivial
 * olsun diye) ama render hatti tek bir metin bekliyor. Ceviriyi burada
 * yapmak, iki farkli render yolu (canli / gecmis) olmasindan iyi: iki yol
 * kacinilmaz olarak ayrisiyor ve gecmis mesajlar canli olanlardan farkli
 * gorunmeye basliyor.
 */
export function HistoryMessage({
  message,
  avatarId,
  foreignAttachments = false,
  onRegenerate,
  onContinue,
  onSwitchVariant,
  onFeedback,
  askedAt,
}: {
  message: Message
  /** Kullanicinin gercek yuklenen fotografi - yoksa "Sen" etiketine duser. */
  avatarId?: string | null
  /** Konusma baska bir kullanicidan kopyalandi: HTML onizlemesi once uyarir. */
  foreignAttachments?: boolean
  onRegenerate?(userMessageId: string): void
  /** Yarida kesilmis yaniti KALDIGI YERDEN surdur (`continue_message_id`). */
  onContinue?(assistantMessageId: string): void
  /** Varyant dallari arasinda gezinme (`< 2/3 >`). */
  onSwitchVariant?(messageId: string, delta: number): void
  /** Oy degisti - liste durumunu guncellemek icin. */
  onFeedback?(messageId: string, next: FeedbackState | null): void
  /**
   * Bu yaniti TETIKLEYEN kullanici mesajinin zamani.
   *
   * Toplam sure bundan sayiliyor: kullanici acisindan bekleme, mesaji
   * gonderdigi anda basliyor - kuyrukta gecen sure de onun bekledigi sure.
   */
  askedAt?: string | null
}) {
  const { t } = useTranslation()

  if (message.role === 'user') {
    const attachments = message.attachments ?? []
    // Sesli mesaj: kayit bir "ek" gibi degil, mesajin KENDISI gibi gorunuyor
    // (oynatici ustte, metni - yani transkript - altinda).
    const voices = attachments.filter((item) => item.kind === 'audio')
    const files = voices.length ? attachments.filter((item) => item.kind !== 'audio') : attachments
    return (
      <div className="msg-row msg-row-user">
        <ProfileAvatar avatarId={avatarId} initial={t('chat.avatar.user')} className="msg-avatar" />
        <article className={voices.length ? 'msg user msg-voice' : 'msg user'}>
          {voices.length > 0 ? (
            <>
              {voices.map((item) => (
                <VoiceNote key={item.id} src={item.url} duration={item.duration_seconds} />
              ))}
              <VoiceTranscript text={message.content} id={message.id} />
            </>
          ) : (
            <MessageBody text={message.content} streaming={false} />
          )}
          {/*
            Ekler metnin ALTINDA: kullanici once ne sordugunu, sonra neyi
            ekledigini goruyor. Ustte olsalar uzun bir resim listesi soruyu
            ekrandan itiyor.
          */}
          <MessageAttachments items={files} foreign={foreignAttachments} />
          <footer className="msg-foot">
            <time className="msg-time" dateTime={message.created_at}>
              {formatMessageTime(message.created_at)}
            </time>
            {/*
              Kendi mesajini kopyalamak: uzun bir istemi baska bir sohbette
              yeniden kullanmanin tek yolu onu elle secmekti (kullanici istegi).
            */}
            <span className="msg-tools">
              <CopyButton text={message.content} label={t('chat.message.copy')} iconOnly />
            </span>
          </footer>
        </article>
      </div>
    )
  }

  // Asistan mesajina bagli ekler (ornegin `local_screenshot` goruntusu) metnin
  // altinda gosteriliyor. ANCAK bir arac kartinda ZATEN gorunenleri
  // (run_python / generate_image ciktilari - kartin `output_attachments`i)
  // haric tutuyoruz: onlar kartta cizildigi icin burada da cizersek CIFT
  // gorunurlerdi. Ekran goruntusu hicbir kartta cizilmedigi icin burada cikar.
  const kartlardaGosterilen = new Set<string>()
  for (const card of message.tool_cards ?? []) {
    const outs = (card as { output_attachments?: unknown }).output_attachments
    if (Array.isArray(outs)) {
      for (const dosya of outs) {
        const id = (dosya as { id?: unknown }).id
        if (typeof id === 'string') kartlardaGosterilen.add(id)
      }
    }
  }
  const ekstraEkler = (message.attachments ?? []).filter((a) => !kartlardaGosterilen.has(a.id))

  return (
    <div className="msg-row">
      <span className="msg-avatar" aria-hidden="true">
        {t('chat.avatar.assistant')}
      </span>
      <article className="msg assistant">
        <MessageBody text={composeMessageText(message)} streaming={false} />
        {ekstraEkler.length > 0 && (
          <MessageAttachments items={ekstraEkler} foreign={foreignAttachments} />
        )}
        <Notice message={message} />
        {/*
          Saat, yanitin BITTIGI an: yarim dakika suren bir yanitta baslangic
          saatini gostermek, kullanicinin ekranda gordugu anla uyusmuyordu.
        */}
        <time className="msg-time" dateTime={message.completed_at ?? message.created_at}>
          {formatMessageTime(message.completed_at ?? message.created_at)}
        </time>
        <footer className="msg-foot">
          <Meta message={message} askedAt={askedAt} />
          <span className="msg-tools">
            {/*
              Kopyalama: YANIT METNI (`content`), `composeMessageText` DEGIL -
              dusunme blogunu ve arac kartlarinin JSON'unu panoya koymak
              kullanicinin isine yaramaz. Yarim kalan yanitta da duruyor:
              kesilen bir metni kurtarmak icin en cok orada gerekiyor.
            */}
            {message.content && (
              <CopyButton text={message.content} label={t('chat.message.copy')} iconOnly />
            )}
            {/*
              Yeniden uret ARTIK yanitin kendisinin altinda - onceki halde
              kullanici mesajinin altindaydi ve orada bir sonraki soruya
              gecince ekrandan kayboluyordu. `parent_message_id` assistant
              mesaji icin HER ZAMAN dolu: `add_assistant_placeholder` onu
              hangi kullanici mesajinin cevabi oldugunu belirterek yaratiyor.
              Durum burada KONTROL EDILMIYOR: kesilmis/basarisiz bir yanitta
              da calismasi gerekiyor - asil kullanim alani tam olarak o.
            */}
            {/*
              "Devam et" YALNIZCA sürdürülecek metin varken. Bos bir kesintide
              (model daha hicbir sey yazmadan kesildi) devam edilecek bir sey
              yok - dogru eylem "Yeniden uret" ve iki dugmeyi birden gostermek
              kullaniciyi calismayacak bir yola cagirirdi.
            */}
            {onContinue &&
              (message.status === 'interrupted' || message.status === 'cancelled') &&
              message.content.trim() !== '' && (
                <button
                  type="button"
                  className="copy"
                  onClick={() => onContinue(message.id)}
                  aria-label={t('chat.message.continue')}
                  title={t('chat.message.continue')}
                >
                  <PlayIcon />
                </button>
              )}
            {onRegenerate && message.parent_message_id && (
              <button
                type="button"
                className="copy"
                onClick={() => onRegenerate(message.parent_message_id as string)}
                aria-label={t('chat.message.regenerate')}
                title={t('chat.message.regenerate')}
              >
                <RefreshIcon />
              </button>
            )}
            {/*
              Oy ve okuma YALNIZCA tamamlanmis yanitlarda. Yarim kalan bir
              yaniti degerlendirmek anlamsiz ve kullaniciyi "yanit kotu" demeye
              yonlendirirdi - oysa sorun yanit degil, kesilme.
            */}
            {onFeedback && message.status === 'complete' && (
              <>
                {/*
                  Okuma `content`i kullaniyor, `composeText()`i DEGIL: dusunme
                  blogunu ve arac kartlarinin JSON'unu sesli okumak anlamsiz
                  bir gurultu olurdu.
                */}
                <SpeakButton text={message.content} />
                <Feedback
                  messageId={message.id}
                  value={message.feedback}
                  onChange={(next) => onFeedback(message.id, next)}
                />
              </>
            )}
          </span>
          {onSwitchVariant && message.variant_count > 1 && (
            <VariantNav message={message} onSwitch={onSwitchVariant} />
          )}
        </footer>
      </article>
    </div>
  )
}

/**
 * Varyant gezinmesi: `< 2/3 >`.
 *
 * Mesaj duzenleme ve yeniden uretme eski yaniti SILMIYOR - ayni
 * `parent_message_id` altinda yeni bir kardes yaratiyor. Bu gezinme olmadan
 * eski dallar veritabaninda DURUYOR ama erisilemiyor, yani kullanici
 * "yeniden uret"e bastiginda onceki yaniti kalici olarak kaybetmis gibi
 * hissediyor.
 */
function VariantNav({
  message,
  onSwitch,
}: {
  message: Message
  onSwitch(messageId: string, delta: number): void
}) {
  const { t } = useTranslation()
  return (
    <span className="variant-nav">
      <button
        type="button"
        className="copy"
        onClick={() => onSwitch(message.id, -1)}
        disabled={message.variant_index <= 1}
        aria-label={t('chat.message.prevVariant')}
      >
        ‹
      </button>
      <span aria-live="polite">
        {message.variant_index}/{message.variant_count}
      </span>
      <button
        type="button"
        className="copy"
        onClick={() => onSwitch(message.id, 1)}
        disabled={message.variant_index >= message.variant_count}
        aria-label={t('chat.message.nextVariant')}
      >
        ›
      </button>
    </span>
  )
}

/**
 * Kesintiye ugramis / basarisiz yanit icin uyari.
 *
 * Kismi metin EKRANDA KALIYOR: silmek, kullanicinin okuyabilecegi bir
 * yanitin yarisini atmak olurdu. Yalnizca ne oldugu soyleniyor.
 */
function Notice({ message }: { message: Message }) {
  const { t } = useTranslation()
  if (message.status === 'complete') return null

  // Kesinti SEBEBI gorunur olmali. Onceki halde tek bir metin vardi ve her
  // kesintide "sunucu yeniden baslatildi" yaziyordu - 25 Eylul'de bir hata
  // yuzunden biten yanitlar "token_limit" ile kesilmis isaretlenince
  // kullanici bitmis yanitin altinda YANLIS bir sebep okudu.
  const interruptedKey =
    message.error_code === 'server_restart'
      ? 'chat.notice.interrupted'
      : message.error_code === 'token_limit'
        ? 'chat.notice.tokenLimit'
        : 'chat.notice.interruptedOther'

  const key =
    message.status === 'interrupted'
      ? interruptedKey
      : message.status === 'cancelled'
        ? 'chat.notice.cancelled'
        : message.status === 'streaming'
          ? 'chat.notice.streaming'
          : 'chat.notice.failed'
  const isWarning = message.status === 'interrupted' || message.status === 'failed'

  return (
    <p className="msg-notice" role="status">
      {isWarning && <WarningIcon />}
      {t(key)}
    </p>
  )
}

function Meta({ message, askedAt }: { message: Message; askedAt?: string | null }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const parts: string[] = []
  if (message.model) {
    const modelKey =
      message.model === 'vl'
        ? 'chat.model.vl'
        : message.model === 'fast'
          ? 'chat.model.fast'
          : 'chat.model.reason'
    parts.push(t(modelKey))
  }
  if (message.stats?.tokens_per_sec) {
    // Sayi bicimi `Intl`e BIRAKILMIYOR: "8.1" teknik bir olcum ve
    // `tok/s` etiketiyle birlikte anlasiliyor. Turkce ondalik ayirici
    // (virgul) uygulamak, olcumu bir yerde "8,1" bir yerde "8.1"
    // gostermek olurdu - loglarla ve metriklerle karsilastirilan bir
    // deger icin tutarlilik daha degerli.
    parts.push(t('chat.meta.tokensPerSec', { value: message.stats.tokens_per_sec.toFixed(1) }))
  }
  if (message.stats?.output_tokens) {
    parts.push(t('chat.meta.tokens', { count: message.stats.output_tokens }))
  }
  // Sure: kullanicinin mesajindan yanitin bitisine. Onceden yalnizca URETIM
  // suresi yaziyordu - kuyrukta ve arac cagrilarinda gecen sure gorunmuyordu,
  // yani kullanicinin bekledigi sure ile yazan sayi tutmuyordu.
  const timeline = buildTimeline(message, askedAt)
  const total = timeline.total ?? message.stats?.seconds ?? null
  if (total !== null) parts.push(formatDuration(total, t))

  if (parts.length === 0) return null
  const detailed = timeline.rows.length > 0
  return (
    <span className="msg-meta">
      {detailed ? (
        <>
          <button
            type="button"
            className="msg-meta-toggle"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {parts.join(' · ')}
          </button>
          {open && (
            <span className="msg-timeline">
              {timeline.rows.map((row) => (
                <span key={row.label} className="msg-timeline-row">
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </span>
              ))}
            </span>
          )}
        </>
      ) : (
        parts.join(' · ')
      )}
    </span>
  )
}

/**
 * Yanitin zaman cizelgesi.
 *
 * Kullanicinin sorusu -> ilk metnin ekrana dusmesi -> yanitin bitisi. Araclar
 * (kod, gorsel) kendi surelerini kartlarina yaziyor; toplamin nereye gittigi
 * boylece gorulebiliyor - "1 dk 34 sn"nin 43 saniyesi gorsel uretimiyse bu
 * bir yavaslik degil, isin kendisi.
 */
function buildTimeline(
  message: Message,
  askedAt?: string | null,
): { total: number | null; rows: { label: string; value: string }[] } {
  const t = i18next.t.bind(i18next)
  const asked = askedAt ? Date.parse(askedAt) : null
  const finished = message.completed_at ? Date.parse(message.completed_at) : null
  const firstToken = message.first_token_at ? Date.parse(message.first_token_at) : null
  const total = asked !== null && finished !== null ? (finished - asked) / 1000 : null
  const rows: { label: string; value: string }[] = []
  if (asked !== null) {
    rows.push({ label: t('chat.timeline.asked'), value: formatMessageTime(askedAt as string) })
  }
  if (asked !== null && firstToken !== null) {
    rows.push({
      label: t('chat.timeline.firstToken'),
      value: formatDuration((firstToken - asked) / 1000, t),
    })
  }
  for (const card of message.tool_cards ?? []) {
    const seconds = typeof card.seconds === 'number' ? card.seconds : null
    if (seconds === null) continue
    const kind = String(card.kind ?? '')
    const label =
      kind === 'image'
        ? t('chat.timeline.toolImage')
        : kind === 'code'
          ? t('chat.timeline.toolCode')
          : kind === 'search'
            ? t('chat.timeline.toolSearch')
            : kind === 'fetch'
              ? t('chat.timeline.toolFetch')
              : t('chat.timeline.toolOther')
    rows.push({ label, value: formatDuration(seconds, t) })
  }
  // "Yazma suresi" ILK TOKEN'DAN sonrasi: `stats.seconds` uretimin TAMAMI
  // (ilk token'i bekleme dahil) ve onu oldugu gibi yazmak satirlarin
  // toplamini bozuyordu - "ilk yanit 1 sn" + "yazma 1 dk 54 sn" iken
  // "toplam" da 1 dk 54 sn cikiyordu, yani ilk yanit iki kez sayiliyordu.
  // Ilk token ani bilinmiyorsa (eski mesajlar) eski davranis korunuyor.
  const writing =
    firstToken !== null && finished !== null
      ? (finished - firstToken) / 1000
      : (message.stats?.seconds ?? null)
  if (writing) {
    rows.push({ label: t('chat.timeline.generation'), value: formatDuration(writing, t) })
  }
  if (total !== null && finished !== null) {
    rows.push({
      label: t('chat.timeline.finished'),
      value: formatMessageTime(message.completed_at as string),
    })
    rows.push({ label: t('chat.timeline.total'), value: formatDuration(total, t) })
  }
  return { total, rows: rows.length > 1 ? rows : [] }
}
